import { CommonModule, Location } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import CountryList from 'country-list-with-dial-code-and-flag'
import CountryFlagSvg from 'country-list-with-dial-code-and-flag/dist/flag-svg'
import { NzFormLayoutType } from 'ng-zorro-antd/form'
import { NzNotificationService } from 'ng-zorro-antd/notification'
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask'
import { map, mergeMap } from 'rxjs/operators'
import { AtomModule } from '../../atom/atom.module'
import { AppService } from '../../service/app.service'
import { AuthenticationService } from '../../service/authentication.service'
import { CountryService } from '../../service/country.service'
import { CustomerService } from '../../service/customer.service'
import { OnboardingService } from '../../service/onboarding.service'
import { strongPasswordPattern } from '../../utils/regex/password.regex'
import { phoneValidator } from '../../utils/validators/phone-validator'
import { NzButtonModule } from 'ng-zorro-antd/button'
import { NzDividerModule } from 'ng-zorro-antd/divider'
import { NzIconModule } from 'ng-zorro-antd/icon'
import { NzInputModule } from 'ng-zorro-antd/input'
import { NzSelectModule } from 'ng-zorro-antd/select'
import { AppleSigninComponent } from '../../molecule/apple-signin/apple-signin.component'
import { GoogleSigninComponent } from '../../molecule/google-signin/google-signin.component'
import { MicrosoftSigninComponent } from '../../molecule/microsoft-signin/microsoft-signin.component'
import { DiscordSigninComponent } from '../../molecule/discord-signin/discord-signin.component'
import { FacebookSigninComponent } from '../../molecule/facebook-signin/facebook-signin.component'
import { of } from 'rxjs'
import { OnboardingProgressState } from '../../gql/generated'
interface Country {
  code: string
  name: string
  dialingCode: string
  flag: SafeHtml
}

/**
 * Onboarding Flow:
 *
 * Third Party Login:
 * CUSTOMER_REGISTER_3PARTY ---exist missing fields---> INFORMATION_FULFILL ---submit form---> SMS_VERIFICATION ---verify success---> COMPLETED
 *
 * Form Register:
 * CUSTOMER_REGISTER ---submit form---> SMS_VERIFICATION ---verify success---> COMPLETED
 *
 */
@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    NzButtonModule,
    NzDividerModule,
    NzInputModule,
    NzSelectModule,
    NzIconModule,
    AtomModule,
    NgxMaskDirective,
    AppleSigninComponent,
    GoogleSigninComponent,
    MicrosoftSigninComponent,
    DiscordSigninComponent,
    FacebookSigninComponent,
  ],
  providers: [provideNgxMask()],
})
export class RegisterComponent implements OnInit {
  validateForm!: FormGroup<{
    formLayout: FormControl<NzFormLayoutType>
    firstName: FormControl<string>
    lastName: FormControl<string>
    email: FormControl<string>
    password: FormControl<string>
    country: FormControl<string>
    zip: FormControl<string>
    city: FormControl<string>
    address: FormControl<string>
    countryCode: FormControl<string>
    phone: FormControl<string>
    eula: FormControl<boolean>
  }>

  passwordVisible = false
  formSubmitted = false
  countryList: Country[] = []
  placeholder = 'Phone number'

  pluginName = ''

  resolveUrl = ''

  constructor(
    private formBuilder: NonNullableFormBuilder,
    private authenticationService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    public appService: AppService,
    private sanitizer: DomSanitizer,
    private location: Location,
    private customerService: CustomerService,
    private countryService: CountryService,
    private onboardingService: OnboardingService,
    private nzNotificationService: NzNotificationService
  ) {
    this.createForm()
  }

  ngOnInit() {
    this.initCountryList()
    this.route.queryParams.subscribe((params) => {
      const provider = params['provider']
      const code = params['code']
      const token = params['token']
      if (provider === 'discord' && code) {
        this.onDiscordToken(code)
        // Remove query parameters from the URL
        this.location.replaceState(this.location.path().split('?')[0])
      }
      if (provider === 'apple' && token) {
        this.onAppleToken(token)
        // Remove query parameters from the URL
        this.location.replaceState(this.location.path().split('?')[0])
      }
      const resolveUrl = params['resolveUrl']
      if (resolveUrl && resolveUrl !== '') {
        this.authenticationService.authStore$.update((state) => {
          return { ...state, resolveUrl }
        })
      }
    })

    this.validateForm
      .get('country')
      ?.valueChanges.subscribe((selectedCountry) => {
        if (selectedCountry) {
          // Update the countryCode field with the same value as country
          this.validateForm.get('countryCode')?.setValue(selectedCountry)

          // Also update the phone placeholder based on the selected country
          this.updatePhonePlaceholder(selectedCountry)
        }
      })
  }

  initCountryList() {
    this.countryList = CountryList.getAll()
      .filter((country) => {
        return ['US', 'DE', 'CN', 'KR', 'UK', 'IN'].includes(country.code)
      })
      .map((country) => ({
        code: country.code,
        name: country.name,
        dialingCode: country.dial_code,
        flag: this.sanitizer.bypassSecurityTrustHtml(
          CountryFlagSvg[country.code as never]
        ),
      }))
  }

  getPhoneMaskForCountry(countryCode: string): string {
    if (!countryCode) {
      return '000000000000' // Default mask
    }

    const masks: { [key: string]: string } = {
      US: '(000) 000-0000', // United States: (123) 456-7890
      DE: '0000 0000000', // Germany: 0123 4567890
      CN: '000 0000 0000', // China: 123 4567 8901
      KR: '00-0000-0000', // South Korea: 10-1234-5678
      UK: '00000 000000', // United Kingdom: 01234 567890
      IN: '00000 00000', // India: 12345 67890
    }

    return masks[countryCode.toUpperCase()] || '000000000000' // Default mask if country not found
  }

  updatePhonePlaceholder(countryCode: string) {
    switch (countryCode.toUpperCase()) {
      case 'US':
        this.placeholder = '(555) 123-4567'
        break
      case 'DE':
        this.placeholder = '0123 4567890'
        break
      case 'CN':
        this.placeholder = '123 4567 8901'
        break
      case 'KR':
        this.placeholder = '10-1234-5678'
        break
      case 'IN':
        this.placeholder = '99999 99999'
        break
      default:
        this.placeholder = 'Phone number'
    }
  }

  getFlagByCode(code: string): SafeHtml {
    const country = this.countryList.find((country) => country.code === code)
    return this.sanitizer.bypassSecurityTrustHtml(
      CountryFlagSvg[country?.code as never]
    )
  }

  getCountryNameByCode(code: string): string {
    const country = this.countryList.find((country) => country.code === code)
    return country?.name || ''
  }

  getDialingCodeByCountryCode(code: string): string {
    const country = this.countryList.find(
      (country) => country.code.toUpperCase() === code.toUpperCase()
    )
    return country?.dialingCode || ''
  }

  createForm() {
    this.validateForm = this.formBuilder.group({
      formLayout: 'horizontal' as NzFormLayoutType,
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.email, Validators.required]],
      password: [
        '',
        [Validators.required, Validators.minLength(8), strongPasswordPattern()],
      ],
      country: ['', [Validators.required]],
      zip: ['', [Validators.required]],
      city: ['', [Validators.required]],
      address: ['', [Validators.required]],
      // dial code, like +1, +44, +49, etc.
      countryCode: ['', [Validators.required]],
      phone: ['', [Validators.required, phoneValidator('countryCode')]],
      eula: [true, [Validators.requiredTrue]],
    })
  }

  // CUSTOMER_REGISTER ---submit form---> SMS_VERIFICATION ---verify success---> COMPLETED
  submitForm() {
    console.log('submitForm')

    this.validateForm.markAllAsTouched()
    this.validateForm.updateValueAndValidity()
    this.validateForm.validate()

    if (this.validateForm.valid) {
      this.customerService.registerCustomerAccount({
        emailAddress: this.validateForm.value.email as string,
        firstName: this.validateForm.value.firstName as string,
        lastName: this.validateForm.value.lastName as string,
        password: this.validateForm.value.password as string,
        phoneNumber: this.getDialingCodeByCountryCode(this.validateForm.value.countryCode as string) + this.validateForm.value.phone,
      }).pipe(
        mergeMap((response) => {
          return this.onboardingService.createOnboarding({
            emailAddress: this.validateForm.value.email as string,
            resolveUrl: this.authenticationService.authStore$.state.resolveUrl,
            state: OnboardingProgressState.CUSTOMER_REGISTER
          }).pipe(map(() => response))
        }),
        mergeMap((response) => {
          return this.onboardingService.updateOnboarding({
            countryCode: this.validateForm.value.country as string,
            zip: this.validateForm.value.zip as string,
            city: this.validateForm.value.city as string,
            address: this.validateForm.value.address as string,
            emailAddress: this.validateForm.value.email as string,
            state: OnboardingProgressState.EMAIL_VERIFICATION
          })
        })
      ).subscribe((response) => {
        if (response) {
          const error = response.data?.updateOnboarding as {
            errorCode?: string
            message?: string
          }
          if (error?.errorCode) {
            this.nzNotificationService.error(
              error.errorCode,
              error.message as string
            )
          } else {
            this.nzNotificationService.success(`Register Successfully`, "You have successfully registered, we have sent you an email, please verify your email address.")
          }
        }
      })
    }
  }

  unsetLastValue() {
    this.validateForm.get('phone')?.setValue('')
  }

  registerClick() {
    console.log('register click')
  }

  onDiscordToken(code: string) {
    this.authenticationService
      .login({
        discord_customer: {
          // the code means OAuth2 authentication code, we need to get the access_token from the server
          token: code,
          page: 'register'
        },
      })
      .subscribe(() => {
        this.appService.hideProgress()
        this.redirectAfterThirdPartyLogin()
      })
  }

  onGoogleToken(token: string) {
    this.authenticationService
      .login({
        google_customer: {
          token: token,
        },
      })
      .subscribe(() => {
        this.appService.hideProgress()
        this.redirectAfterThirdPartyLogin()
      })
  }

  onFacebookToken(token: string) {
    console.log('facebook token', token)
  }

  onMicrosoftToken(token: string) {
    this.authenticationService
      .login({
        microsoft_customer: {
          token: token,
        },
      })
      .subscribe(() => {
        this.appService.hideProgress()
        this.redirectAfterThirdPartyLogin()
      })
  }

  onAppleToken(token: string) {
    this.authenticationService
    .login({
      apple_customer: {
        token: token,
      },
    })
    .subscribe(() => {
      this.appService.hideProgress()
      this.redirectAfterThirdPartyLogin()
    })
  }

  redirectAfterThirdPartyLogin() {
    this.customerService
      .getActiveCustomer()
      .pipe(
        mergeMap((response) => {
          if (response?.data?.activeCustomer?.id) {
            return this.onboardingService.createOnboarding({
              emailAddress: response?.data?.activeCustomer?.emailAddress,
              resolveUrl: this.authenticationService.authStore$.state.resolveUrl,
              state: OnboardingProgressState.CUSTOMER_REGISTER_3PARTY
            })
          } else {
            return of(null)
          }
        })
      )
      .subscribe((response) => {
        if (response) {
          const error = response.data?.createOnboarding as {
            errorCode?: string
            message?: string
          }
          if (error?.errorCode) {
            this.nzNotificationService.error(
              error.errorCode,
              error.message as string
            )
          } else {
            this.router.navigate(['/register-complete'])
          }
        }
      })
  }
}
